import { useQuery } from '@tanstack/react-query';
import {
  getDownloadsByVersion,
  getMetricsOverview,
  getModerationMetrics,
  getRevenueByPlan,
  getSignupsTimeseries,
  type TimeseriesParams,
} from '../lib/metrics-api.js';

/* Hooks de métricas (ADR-0353/0359). `refetchInterval` alinhado ao cache de 60s do
   backend (polling mais curto só geraria requests redundantes). TanStack Query v5 já
   pausa o polling em aba oculta por default (`refetchIntervalInBackground: false`).

   `enabled` permite que o dashboard em abas dispare cada query só quando a aba dona está
   ativa (lazy-load) — evita renderizar 3 charts + N cards + moderação de uma vez. */

interface QueryToggle {
  /** Default `true`. A aba passa `activeTab === X` para adiar a query até ficar visível. */
  readonly enabled?: boolean;
}

export function useMetricsOverview(options: QueryToggle = {}) {
  return useQuery({
    queryKey: ['metrics', 'overview'],
    queryFn: getMetricsOverview,
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useDownloadsByVersion(options: QueryToggle = {}) {
  return useQuery({
    queryKey: ['metrics', 'downloads-by-version'],
    queryFn: getDownloadsByVersion,
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useModerationMetrics(options: QueryToggle = {}) {
  return useQuery({
    queryKey: ['metrics', 'moderation'],
    queryFn: getModerationMetrics,
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useRevenueByPlan(options: QueryToggle = {}) {
  return useQuery({
    queryKey: ['metrics', 'revenue-by-plan'],
    queryFn: getRevenueByPlan,
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useSignupsTimeseries(params: TimeseriesParams, options: QueryToggle = {}) {
  return useQuery({
    queryKey: ['metrics', 'timeseries', 'signups', params.from, params.to, params.grain],
    queryFn: () => getSignupsTimeseries(params),
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
}
